import clsx from "clsx";
import { ReactNode } from "react";
import {
  FormProvider,
  RegisterOptions,
  useForm,
  useFormContext,
} from "react-hook-form";

const INPUT_CLASSES =
  "h-9 rounded border-dm-charcoal-200 px-2 py-0 text-sm invalid:border-dm-error-500 disabled:bg-dm-charcoal-100/50 disabled:text-dm-charcoal-400";

export const ManagerForm: React.FC<{
  onSubmit: (data: any) => void;
  className?: string;
  defaultValues?: any;
}> = ({ onSubmit, defaultValues, className, children }) => {
  const methods = useForm({ defaultValues });
  const { handleSubmit } = methods;
  return (
    <FormProvider {...methods}>
      <form className={className} onSubmit={handleSubmit(onSubmit)}>
        {children}
      </form>
    </FormProvider>
  );
};

const ManagerFormFieldWrapper: React.FC<{
  fieldName: string;
  label: string;
}> = ({ children }) => (
  <div className="flex w-full flex-col gap-2">{children}</div>
);

const ManagerFormFieldLabel: React.FC<{
  htmlFor: string;
  required?: boolean;
}> = ({ children, htmlFor, required = false }) => (
  <label className="text-sm font-bold" htmlFor={htmlFor}>
    {children}
    {required && <span className="ml-1 text-dm-brand-blue-500">*</span>}
  </label>
);

const ManagerFormHelpText: React.FC = ({ children }) => (
  <p className="-mt-1 text-xs text-dm-charcoal-500">{children}</p>
);

const FieldError: React.FC<{ fieldName: string }> = ({ fieldName }) => {
  const {
    formState: { errors },
  } = useFormContext();
  const error = errors[fieldName];
  return error?.message ? (
    <p className="text-xs text-dm-error-500">{error.message}</p>
  ) : (
    <></>
  );
};

export const ManagerFormTextField: React.FC<{
  fieldName: string;
  label: string;
  type?: "text" | "email" | "password";
  options?: RegisterOptions;
  required?: boolean;
  disabled?: boolean;
  helpText?: string | ReactNode;
}> = ({
  fieldName,
  label,
  type = "text",
  options = {},
  required = false,
  disabled = false,
  helpText,
}) => {
  const {
    register,
    formState: { errors },
  } = useFormContext();
  return (
    <ManagerFormFieldWrapper fieldName={fieldName} label={label}>
      <ManagerFormFieldLabel htmlFor={fieldName} required={required}>
        {label}
      </ManagerFormFieldLabel>
      {helpText && <ManagerFormHelpText>{helpText}</ManagerFormHelpText>}
      <input
        className={clsx(
          INPUT_CLASSES,
          errors[fieldName] &&
            "border-dm-error-500 focus:border-dm-error-500 focus:ring-dm-error-500"
        )}
        id={fieldName}
        type={type}
        {...register(fieldName, {
          required: required ? "This field is required" : false,
          ...options,
        })}
        disabled={disabled}
      />
      <FieldError fieldName={fieldName} />
    </ManagerFormFieldWrapper>
  );
};

export const ManagerFormNumberField: React.FC<{
  fieldName: string;
  label: string;
  min?: number;
  max?: number;
  options?: RegisterOptions;
  required?: boolean;
  disabled?: boolean;
  helpText?: string | ReactNode;
}> = ({
  fieldName,
  label,
  min,
  max,
  options = {},
  required = false,
  disabled = false,
  helpText,
}) => {
  const {
    register,
    formState: { errors },
  } = useFormContext();
  const optionsFinal: RegisterOptions = {
    ...(min !== undefined
      ? { min: { value: min, message: `This must be at least ${min}` } }
      : {}),
    ...(max !== undefined
      ? { max: { value: max, message: `This must be no more than ${max}` } }
      : {}),
    required: required ? "This field is required" : false,
    ...options,
    pattern: undefined,
    valueAsNumber: true,
    valueAsDate: false,
  };
  return (
    <ManagerFormFieldWrapper fieldName={fieldName} label={label}>
      <ManagerFormFieldLabel htmlFor={fieldName} required={required}>
        {label}
      </ManagerFormFieldLabel>
      {helpText && <ManagerFormHelpText>{helpText}</ManagerFormHelpText>}
      <input
        className={clsx(
          INPUT_CLASSES,
          errors[fieldName] &&
            "border-dm-error-500 focus:border-dm-error-500 focus:ring-dm-error-500"
        )}
        id={fieldName}
        type="number"
        min={min}
        max={max}
        disabled={disabled}
        {...register(fieldName, optionsFinal)}
      />
      <FieldError fieldName={fieldName} />
    </ManagerFormFieldWrapper>
  );
};

export const ManagerFormSelectField: React.FC<{
  fieldName: string;
  label: string;
  choices: { value: string; label: string }[];
  options?: RegisterOptions;
  required?: boolean;
  disabled?: boolean;
  helpText?: string | ReactNode;
}> = ({
  fieldName,
  label,
  choices,
  options = {},
  required = false,
  disabled = false,
  helpText,
}) => {
  const {
    register,
    formState: { errors },
  } = useFormContext();
  return (
    <ManagerFormFieldWrapper fieldName={fieldName} label={label}>
      <ManagerFormFieldLabel htmlFor={fieldName} required={required}>
        {label}
      </ManagerFormFieldLabel>
      {helpText && <ManagerFormHelpText>{helpText}</ManagerFormHelpText>}
      <select
        className={clsx(
          INPUT_CLASSES,
          errors[fieldName] &&
            "border-dm-error-500 focus:border-dm-error-500 focus:ring-dm-error-500"
        )}
        id={fieldName}
        {...register(fieldName, {
          required: required ? "This field is required" : false,
          ...options,
        })}
        disabled={disabled}
      >
        {choices.map((choice) => (
          <option key={choice.value} value={choice.value}>
            {choice.label}
          </option>
        ))}
      </select>
      <FieldError fieldName={fieldName} />
    </ManagerFormFieldWrapper>
  );
};
